import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { listIncidents, type Incident, type IncidentListFilter, type AttackCategory, type IncidentStatus } from '../../services/incidents';
import { AttackCategoryBadge } from '../../components/AttackCategoryBadge';
import { SeverityIndicator } from '../../components/SeverityIndicator';
import { CrossCorrelationLinks } from '../../components/CrossCorrelationLinks';

const CATEGORIES: AttackCategory[] = [
  'PACKET_SNIFFER', 'MEMORY_HACK', 'DEBUGGER', 'INJECTION',
  'GAME_TAMPERING', 'KERNEL_TOOLING', 'RECON', 'OVERLAY',
  'TIMING', 'INTEGRITY', 'UNKNOWN',
];

const STATUSES: IncidentStatus[] = [
  'open', 'reviewed_ok', 'reviewed_fp', 'escalated',
  'auto_acted_pending_review', 'resolved',
];

export function Incidents() {
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [filter, setFilter] = useState<IncidentListFilter>({ limit: 50 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const resp = await listIncidents(filter);
      setIncidents(resp.data);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">Incidents</h1>

      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <div>
          <label className="block text-xs text-gray-400">Status</label>
          <select
            value={filter.status ?? ''}
            onChange={(e) => setFilter({ ...filter, status: (e.target.value || undefined) as IncidentStatus | undefined })}
            className="bg-gray-800 text-gray-100 px-2 py-1 rounded"
          >
            <option value="">All</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-400">Category</label>
          <select
            value={filter.attack_category ?? ''}
            onChange={(e) => setFilter({ ...filter, attack_category: (e.target.value || undefined) as AttackCategory | undefined })}
            className="bg-gray-800 text-gray-100 px-2 py-1 rounded"
          >
            <option value="">All</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-400">Severity min</label>
          <input
            type="number" min={0} max={100}
            value={filter.severity_min ?? ''}
            onChange={(e) => setFilter({ ...filter, severity_min: e.target.value ? Number(e.target.value) : undefined })}
            className="bg-gray-800 text-gray-100 px-2 py-1 rounded w-20"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-400">HWID</label>
          <input
            type="text"
            value={filter.hwid ?? ''}
            onChange={(e) => setFilter({ ...filter, hwid: e.target.value || undefined })}
            className="bg-gray-800 text-gray-100 px-2 py-1 rounded w-48"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-400">Account ID</label>
          <input
            type="text"
            value={filter.account_id ?? ''}
            onChange={(e) => setFilter({ ...filter, account_id: e.target.value || undefined })}
            className="bg-gray-800 text-gray-100 px-2 py-1 rounded w-32"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-400">IP</label>
          <input
            type="text"
            value={filter.source_ip ?? ''}
            onChange={(e) => setFilter({ ...filter, source_ip: e.target.value || undefined })}
            className="bg-gray-800 text-gray-100 px-2 py-1 rounded w-32"
          />
        </div>
        <button
          onClick={refresh}
          disabled={loading}
          className="px-4 py-1 rounded bg-blue-600 hover:bg-blue-500 text-white"
        >
          {loading ? 'Loading…' : 'Apply'}
        </button>
      </div>

      {error && <div className="text-red-400 mb-3">Error: {error}</div>}

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm border border-gray-800">
          <thead className="bg-gray-900 text-gray-300">
            <tr>
              <th className="text-left p-2">Category</th>
              <th className="text-left p-2">Severity</th>
              <th className="text-right p-2">Events</th>
              <th className="text-right p-2">Occurrences</th>
              <th className="text-left p-2">Status</th>
              <th className="text-left p-2">HWID</th>
              <th className="text-left p-2">Account</th>
              <th className="text-left p-2">IP</th>
              <th className="text-left p-2">Last seen</th>
              <th className="text-left p-2">Cross-link</th>
              <th className="text-left p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {incidents.map((in_) => (
              <tr key={in_.id} className="border-t border-gray-800 hover:bg-gray-900">
                <td className="p-2"><AttackCategoryBadge category={in_.attack_category} /></td>
                <td className="p-2"><SeverityIndicator severity={in_.severity_max} /></td>
                <td className="p-2 text-right">{in_.event_count}</td>
                <td className="p-2 text-right">{in_.occurrence_sum}</td>
                <td className="p-2">
                  <span className="text-xs px-2 py-0.5 rounded bg-gray-800">{in_.status}</span>
                </td>
                <td className="p-2 font-mono text-xs">{in_.hwid?.slice(0, 16) || '—'}</td>
                <td className="p-2 text-xs">{in_.account_id || '—'}</td>
                <td className="p-2 font-mono text-xs">{in_.source_ip || '—'}</td>
                <td className="p-2 text-xs">{in_.last_seen_at}</td>
                <td className="p-2">
                  <CrossCorrelationLinks hwid={in_.hwid} account_id={in_.account_id} ip={in_.source_ip} />
                </td>
                <td className="p-2">
                  <Link to={`/incidents/${in_.id}`} className="text-blue-400 hover:underline text-xs">
                    Details
                  </Link>
                </td>
              </tr>
            ))}
            {incidents.length === 0 && !loading && (
              <tr><td colSpan={11} className="p-6 text-center text-gray-400">Sem incidents</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Incidents;
